import DynamicSectionElement from '@/js/dynamic-section-element';
import { initKyotoOnlyProduct } from '@/js/kyoto-only-actions.js';
import { initCountdownTimer } from '@/js/countdown-timer-actions.js';
import { getDelta } from '@/js/countdown-timer-actions.js';

/**
 * A custom element that represents the add to cart button
 * @extends DynamicSectionElement
 * @element product-add-cta
 *
 */
class ProductAddCTA extends DynamicSectionElement {
  static get observedAttributes() {
    return ['ctaText'];
  }

  connectedCallback() {
    this.setup();
    this.b2bSpecificFunctionality();
  }

  /**
   * Initializes the variables
   * @returns {void}
   */
  initVariables() {
    this.button = this.querySelector('button');
    this.bundleable = !!this.button.closest('[data-bundleable]');
    this.launchDate = this.button.dataset.launchDate;
    this.delta = getDelta(this.launchDate);
    this.kyotoTranslations = globalThis.PxlApp.translations.kyotoOnlyProduct;
    this.countdownTranslations = globalThis.PxlApp.translations.countdownTimer;
    this.storeType = this.dataset.storeType;
    this.launchDateMs = Date.parse(this.launchDate);
    this.pastLaunchDate =
      Number.isNaN(this.launchDateMs) || Date.now() > this.launchDateMs;
  }

  /**
   * Disables the button and updates the text
   * @returns {void}
   */
  disableButton() {
    this.button.disabled = true;
    this.setTextBasedOnButtonState(true);
  }

  /**
   * Enables the button and updates the text
   * @returns {void}
   */
  enableButton() {
    this.button.removeAttribute('disabled');
    this.setTextBasedOnButtonState(false);
  }

  /**
   * Sets the text based on the button state
   * @param {boolean} isDisabled
   */
  setTextBasedOnButtonState(isDisabled) {
    const { comingSoonText, addToCartText, accessDeniedText } =
      this.countdownTranslations;
    const { kyotoOnlyText, bundleText, soldOutText } = this.kyotoTranslations;
    const ctaText = this.button.dataset.buttonText || addToCartText;
    const hasAccessDenied = Object.hasOwn(this.button.dataset, 'accessDenied');
    const maxInventoryQuantity = this.button.getAttribute(
      'max-inventory-quantity'
    );
    if (maxInventoryQuantity && Number.parseInt(maxInventoryQuantity) === 0) {
      this.setText(soldOutText);
      return;
    }

    if (hasAccessDenied) {
      this.setText(accessDeniedText);
      return;
    }

    this.setText(
      this.getButtonText(
        isDisabled,
        bundleText,
        ctaText,
        kyotoOnlyText,
        comingSoonText
      )
    );
  }

  /**
   * Initializes the variables and translations
   */
  setup() {
    this.initVariables();
    initKyotoOnlyProduct(this);

    // If the launch date is in the past or not set, there is no need to initialize the countdown timer or alter the CTA text
    if (this.pastLaunchDate) return;
    initCountdownTimer(this);
  }

  /**
   * Gets the text for the button
   * @param {boolean} isDisabled
   * @param {string} bundleText
   * @param {string} ctaText
   * @param {string} kyotoOnlyText
   * @param {string} comingSoonText
   * @returns {string}
   */
  getButtonText(
    isDisabled,
    bundleText,
    ctaText,
    kyotoOnlyText,
    comingSoonText
  ) {
    if (!isDisabled) {
      return this.bundleable ? bundleText : ctaText;
    }
    if (this.button.hasAttribute('kyoto-only-product')) {
      return kyotoOnlyText;
    }
    return this.delta > 0 ? comingSoonText : bundleText;
  }

  /**
   *  Sets the text of the CTA button
   * @param {string} newText
   */
  setText(newText) {
    this.setAttribute('ctaText', newText);
    this.updateCtaText(newText);
  }

  /**
   * Updates the text of the CTA button
   * @param {string} newText - The new text for the CTA button
   */
  updateCtaText(newText) {
    this.button.innerHTML = newText;
  }

  /**
   * Hides the CTA button when on the b2b store whenever the launch date is in the future
   * @returns {void}
   */
  b2bOnlyHideCTA() {
    this.classList.add('hidden');
  }

  /**
   * A function that contains the b2b specific functionality
   */
  b2bSpecificFunctionality() {
    // Only apply to the b2b store
    if (this.storeType !== 'b2b') {
      return;
    }

    // Hide the CTA button if the launch date is in the future as we have the quick add
    if (this.delta <= 0 || Number.isNaN(this.delta)) {
      this.b2bOnlyHideCTA();
    }
  }
}

customElements.get('product-add-cta') ||
  customElements.define('product-add-cta', ProductAddCTA);

export default ProductAddCTA;
